// Airport reference data for the frontend display components.
//
// Derived from the backend `backend/src/utils/airportData.ts` dataset (mirrored
// here so the frontend never imports backend code). Used to render city-first
// airport labels: "Dallas (DFW)" with the full airport name on hover/title.

export interface AirportInfo {
  /** City for grouping / primary display, e.g. "Dallas". */
  city: string;
  /** Full airport name (no code), e.g. "Dallas/Fort Worth Intl". */
  name: string;
}

export const AIRPORTS: Record<string, AirportInfo> = {
  // Dallas
  DFW: { city: "Dallas", name: "Dallas/Fort Worth Intl" },
  DAL: { city: "Dallas", name: "Dallas Love Field" },
  // New York
  JFK: { city: "New York", name: "John F. Kennedy Intl" },
  LGA: { city: "New York", name: "LaGuardia" },
  EWR: { city: "Newark", name: "Newark Liberty Intl" },
  // Chicago
  ORD: { city: "Chicago", name: "O'Hare Intl" },
  MDW: { city: "Chicago", name: "Midway Intl" },
  // Los Angeles
  LAX: { city: "Los Angeles", name: "Los Angeles Intl" },
  BUR: { city: "Burbank", name: "Hollywood Burbank" },
  LGB: { city: "Long Beach", name: "Long Beach Airport" },
  // San Francisco Bay
  SFO: { city: "San Francisco", name: "San Francisco Intl" },
  OAK: { city: "Oakland", name: "Oakland Intl" },
  SJC: { city: "San Jose", name: "San Jose Intl" },
  // Washington DC
  DCA: { city: "Washington", name: "Reagan National" },
  IAD: { city: "Washington", name: "Dulles Intl" },
  BWI: { city: "Baltimore", name: "Baltimore/Washington Intl" },
  // Houston
  IAH: { city: "Houston", name: "George Bush Intercontinental" },
  HOU: { city: "Houston", name: "William P. Hobby" },
  // Miami / Fort Lauderdale
  MIA: { city: "Miami", name: "Miami Intl" },
  FLL: { city: "Fort Lauderdale", name: "Fort Lauderdale-Hollywood Intl" },
  PBI: { city: "West Palm Beach", name: "Palm Beach Intl" },
  // Boston
  BOS: { city: "Boston", name: "Logan Intl" },
  // Tokyo
  NRT: { city: "Tokyo", name: "Tokyo Narita Intl" },
  HND: { city: "Tokyo", name: "Tokyo Haneda Intl" },
  // Pacific Northwest / Alaska
  SEA: { city: "Seattle", name: "Seattle-Tacoma Intl" },
  PDX: { city: "Portland", name: "Portland Intl" },
  ANC: { city: "Anchorage", name: "Ted Stevens Anchorage Intl" },
  YVR: { city: "Vancouver", name: "Vancouver Intl" },
  // Other major / single-airport cities
  LAS: { city: "Las Vegas", name: "Harry Reid Intl" },
  MCO: { city: "Orlando", name: "Orlando Intl" },
  ATL: { city: "Atlanta", name: "Hartsfield-Jackson Intl" },
  SAT: { city: "San Antonio", name: "San Antonio Intl" },
  AUS: { city: "Austin", name: "Austin-Bergstrom Intl" },
  DEN: { city: "Denver", name: "Denver Intl" },
  PHX: { city: "Phoenix", name: "Phoenix Sky Harbor Intl" },
  MSP: { city: "Minneapolis", name: "Minneapolis-St. Paul Intl" },
  DTW: { city: "Detroit", name: "Detroit Metro Wayne County" },
  CLT: { city: "Charlotte", name: "Charlotte Douglas Intl" },
  PHL: { city: "Philadelphia", name: "Philadelphia Intl" },
  ABQ: { city: "Albuquerque", name: "Albuquerque Intl Sunport" },
  ELP: { city: "El Paso", name: "El Paso Intl" },
  SAN: { city: "San Diego", name: "San Diego Intl" },
  SJU: { city: "San Juan", name: "Luis Munoz Marin Intl" },
  BNA: { city: "Nashville", name: "Nashville Intl" },
  RDU: { city: "Raleigh", name: "Raleigh-Durham Intl" },
  RSW: { city: "Fort Myers", name: "Southwest Florida Intl" },
  SLC: { city: "Salt Lake City", name: "Salt Lake City Intl" },
  STL: { city: "St. Louis", name: "St. Louis Lambert Intl" },
  TPA: { city: "Tampa", name: "Tampa Intl" },
  PIT: { city: "Pittsburgh", name: "Pittsburgh Intl" },
  MSY: { city: "New Orleans", name: "Louis Armstrong Intl" },
  HNL: { city: "Honolulu", name: "Daniel K. Inouye Intl" },
};

/**
 * Resolve airport info for a code. Unknown / empty codes return a safe object
 * whose `city` and `name` are the raw code, so callers never crash.
 */
export function getAirportInfo(code: string | null | undefined): AirportInfo {
  const key = (code || "").trim().toUpperCase();
  return AIRPORTS[key] || { city: key, name: key };
}

/** True when we have curated data (city + full name) for this code. */
export function isKnownAirport(code: string | null | undefined): boolean {
  const key = (code || "").trim().toUpperCase();
  return Boolean(AIRPORTS[key]);
}
